<script setup lang="ts">
import axios from 'axios'
import { Loader2, Send } from 'lucide-vue-next'
import type { ActionsCRUD } from './UsersPage.vue'

import type { Store } from '../../contests/Components/types/global'

const { t } = useI18n()
const { isEmailValid } = useRegex()
const { user, permissions, languages } = useLocalStorage()
const notification = useNotification()
const { userMergePermissions } = useAccess()

interface StoreSelector {
  id: number
  name: string
  checked: boolean
}

interface RoleTemplate {
  id: number
  text: string
}

export type User = {
  id: number | null
  name: string
  roleTemplate: RoleTemplate
  email: string
  stores: number[] | StoreSelector[]
  languageId: { value: number; image: string }
}

const props = withDefaults(
  defineProps<{
    userData?: User | null
    stores: Store[]
    roles: RoleTemplate[]
    modalType: ActionsCRUD
    resendMail: boolean
    isOpen: boolean
  }>(),
  {
    userData: null,
    resendMail: false,
    isOpen: false
  }
)

const merchant = ref(user.value.merchant)

const emit = defineEmits<(e: 'closeModal', cancel?: boolean) => void>()

const isProcessing = ref(false)
const emailError = computed(() => {
  return isEmailValid(userFormData.value.email) ? null : t('pages.settings.usersPage.crud.mailError')
})

const newUser = {
  id: null,
  name: '',
  roleTemplate: props.roles[0],
  email: '',
  stores: [],
  languageId: {
    value: 21,
    image: ''
  }
}

const userFormData = ref<User>(null)
const isMailSent = ref(false)
const selectedStores = ref<StoreSelector[]>([])

const availableLanguages = computed(() =>
  languages.value?.map((language) => {
    language = {
      ...language,
      id: language.iso639_1,
      text: language.nativeName
    }
    return language
  })
)

const computedSelectedLanguage = computed(() => {
  const languageId = props.modalType === 'create' ? merchant.value.fallbackLanguageId : props.userData?.languageId.value

  return languages.value?.find((language) => language.id === languageId)
})

const selectedLanguage = ref(computedSelectedLanguage.value)

const switchLanguage = (id: number) => {
  selectedLanguage.value = languages.value.find((language) => language.iso639_1 === id)
}

onMounted(async () => {
  if (props.userData !== null) {
    userFormData.value = {
      ...props.userData,
      stores: props.userData.stores.map((store) => store.id)
    }
  } else {
    userFormData.value = { ...newUser }
  }
  const { data } = await axios.get('stores')
  selectedStores.value = props.stores.map((store) =>
    Object.assign(store, {
      id: store.id,
      name: store.name,
      checked: data.data.find((userStore) => userStore.id === store.id)
    })
  )
})

const userRoleLabel = computed(() => userFormData.value?.roleTemplate?.text)
const updateUserRole = (id) => {
  userFormData.value.roleTemplate = formatRoleList().find((roleTemplate) => roleTemplate.id === id)
}

const checkAll = () => {
  uncheckAll()
  userFormData.value.stores = props.stores.map((store) => store.id)
}
const uncheckAll = () => {
  userFormData.value.stores = []
}

const userParams = computed(() => {
  const params = {
    id: userFormData.value?.id,
    name: userFormData.value?.name,
    email: userFormData.value?.email,
    storeIds: userFormData.value?.stores,
    roleId: 3,
    roleTemplateId: userFormData.value?.roleTemplate.id,
    merchantId: merchant.value?.id,
    languageId: selectedLanguage.value?.id ?? newUser.languageId.value
  }
  return params
})

const isSelfUpdate = computed(() => props.modalType === 'update' && user.value.id === userFormData.value.id)

const addUser = async () => {
  isProcessing.value = true
  try {
    if (emailError.value) {
      return notification.create('error', {
        text: t('pages.settings.usersPage.crud.mailError'),
        timeout: 2000
      })
    }
    if (userFormData.value.stores.length === 0) {
      return notification.create('error', {
        text: t('pages.settings.usersPage.crud.missingStores'),
        timeout: 2000
      })
    }
    await axios.post(`/users`, {
      ...userParams.value,
      sendFirstMail: true
    })
    isMailSent.value = true
  } catch (error) {
    notification.create('error', {
      text: error?.response?.data?.message ?? t('pages.settings.usersPage.crud.notification.create.error'),
      timeout: 2000
    })
  } finally {
    isProcessing.value = false
  }
}

const updateUser = async () => {
  isProcessing.value = true
  try {
    if (emailError.value) {
      return notification.create('error', {
        text: t('pages.settings.usersPage.crud.mailError'),
        timeout: 2000
      })
    }
    await axios.put(`users/${userFormData.value.id}`, {
      ...userParams.value,
      sendFirstMail: props.resendMail
    })

    if (isSelfUpdate.value) {
      await updateCurrentUserPermissions()
    }
    closeModal(props.resendMail)

    notification.create('success', {
      text: props.resendMail
        ? t('pages.settings.usersPage.invitation.notificationSuccess')
        : t('pages.settings.usersPage.crud.notification.update.success'),
      timeout: 2000
    })
  } catch (error) {
    const defaultErrorText = props.resendMail
      ? t('pages.settings.usersPage.invitation.notificationError')
      : t('pages.settings.usersPage.crud.notification.update.error')
    notification.create('error', {
      text: error?.response?.data?.message ?? defaultErrorText,
      timeout: 2000
    })
  } finally {
    isProcessing.value = false
  }
}

const updateCurrentUserPermissions = async () => {
  try {
    const { data } = await axios.get(`/users/permissions`)
    permissions.value = userMergePermissions(data.data)
  } catch (error) {
    throw new Error(error)
  }
}

const closeModal = (cancel = true) => {
  emit('closeModal', cancel)
}

const deleteUser = async () => {
  isProcessing.value = true
  try {
    await axios.delete(`/users/${props.userData?.id}`)
    closeModal(false)
    notification.create('success', {
      text: t('pages.settings.usersPage.delete.success'),
      timeout: 2000
    })
  } catch (error) {
    notification.create('error', {
      text: error.response?.data?.message ?? t('pages.settings.usersPage.delete.error'),
      timeout: 2000
    })
  } finally {
    isProcessing.value = false
  }
}

const isRoleDropdownDisabled = ref(false)

const formatRoleList = () => {
  if (user.value.roleTemplate.id === 4) return props.roles

  if (props.modalType === 'update' && userFormData.value.roleTemplate.id === 4) {
    isRoleDropdownDisabled.value = true
    return props.roles
  }
  return props.roles.filter((role) => role.id !== 4)
}
</script>

<template>
  <BaseModal
    v-if="resendMail"
    :is-open="isOpen"
    :text-title="t('pages.settings.usersPage.invitation.resendTitle')"
    icon="HeroEnvelopeIconSolid"
    icon-color="green"
    :cancel="t('global.words.no')"
    :link-text="t('global.words.send')"
    @close="closeModal"
    @click="updateUser"
  >
    <template #text>
      <i18n-t
        tag="p"
        class="mb-6 text-sm font-medium text-muted-foreground"
        keypath="pages.settings.usersPage.invitation.resendContent"
      >
        <template #email>
          {{ userFormData?.email }}
        </template>
        <template #newline>
          <br />
        </template>
      </i18n-t>
    </template>
  </BaseModal>
  <template v-else-if="modalType !== 'delete'">
    <BaseModal
      :is-open="!isMailSent"
      :heading-title="
        modalType === 'create' ? t('pages.settings.usersPage.addUser') : t('pages.settings.usersPage.editUser')
      "
      @close="closeModal"
    >
      <AppSkeleton v-if="!userFormData" />
      <div v-else class="flex flex-col text-left">
        <div class="w-full">
          <AppFormInput
            id="nameInput"
            v-model="userFormData.name"
            :label="t('pages.settings.usersPage.crud.name')"
            type="text"
            class="mb-5 mt-0"
          />
          <div class="mb-5">
            <AppFormInput
              id="emailInput"
              v-model="userFormData.email"
              :label="t('pages.settings.usersPage.crud.mail')"
              type="email"
            />
            <span v-if="emailError && userFormData.email.length > 0" class="w-full text-xs text-red-600">{{
              emailError
            }}</span>
          </div>
          <label
            v-if="selectedLanguage"
            class="mb-1 block w-full text-left text-sm font-medium text-foreground"
            for="role"
            >{{ t('pages.settings.usersPage.crud.language') }}</label
          >
          <ComboboxSelect
            v-if="selectedLanguage"
            value-key="id"
            label-key="text"
            class="mb-6 mt-1"
            :placeholder="selectedLanguage?.nativeName"
            :options="availableLanguages"
            trigger-class="w-full"
            :model-value="selectedLanguage?.iso639_1"
            @update:model-value="switchLanguage"
          />
          <label class="mb-1 block w-full text-left text-sm font-medium text-foreground" for="role">{{
            t('pages.settings.usersPage.crud.role')
          }}</label>
          <ComboboxSelect
            id="role"
            :model-value="userFormData.roleTemplate?.id"
            value-key="id"
            label-key="text"
            class="mb-6 mt-1"
            :class="{ 'text-muted-foreground': isRoleDropdownDisabled }"
            :disabled="isRoleDropdownDisabled"
            :options="formatRoleList()"
            trigger-class="w-full"
            :placeholder="userRoleLabel"
            @update:model-value="updateUserRole($event as number)"
          />
          <div class="my-2 text-sm">
            {{ t('pages.settings.usersPage.crud.stores') }}
            <button type="button" class="text-blue-600 hover:text-blue-800 focus:outline-hidden" @click="checkAll">
              {{ t('global.checkboxes.checkAll') }}
            </button>
            /
            <button type="button" class="text-blue-600 hover:text-blue-800 focus:outline-hidden" @click="uncheckAll">
              {{ t('global.checkboxes.uncheckAll') }}
            </button>
          </div>
          <ul class="grid grid-cols-2 gap-y-4 py-5">
            <li v-for="store in selectedStores" :key="store.id">
              <AppCheckboxField
                :id="`store${store.id}`"
                v-model="userFormData.stores"
                :label="store.name"
                :value="store.id"
              />
            </li>
          </ul>
        </div>
      </div>
      <template #linkBtn>
        <div class="flex items-center justify-end gap-4">
          <Button type="button" variant="outline" @click="closeModal">
            {{ t('global.words.cancel') }}
          </Button>
          <Button
            type="button"
            variant="default-primary"
            :disabled="isProcessing"
            @click="userFormData?.id !== null ? updateUser() : addUser()"
          >
            <Loader2 v-if="isProcessing" class="animate-spin" />
            {{ modalType === 'create' ? t('pages.settings.usersPage.crud.send') : t('global.words.save') }}
            <Send v-if="modalType === 'create' && !isProcessing" />
          </Button>
        </div>
      </template>
    </BaseModal>
    <BaseModal
      :is-open="isMailSent"
      icon="HeroEnvelopeIconSolid"
      :text-title="t('pages.settings.usersPage.invitation.title')"
      :link-text="t('pages.settings.usersPage.invitation.receivedButton')"
      @close="closeModal"
      @click="closeModal"
    >
      <template #text>
        <div>
          <i18n-t
            tag="p"
            class="mb-6 text-sm font-medium text-muted-foreground"
            keypath="pages.settings.usersPage.invitation.sent"
          >
            <template #email>
              {{ userFormData?.email }}
            </template>
            <template #newline>
              <br />
            </template>
          </i18n-t>
        </div>
      </template>
    </BaseModal>
  </template>
  <BaseModal
    v-else-if="modalType === 'delete'"
    :is-open="isOpen"
    icon="HeroTrashIconOutline"
    icon-color="red"
    :text-title="t('pages.settings.usersPage.delete.title')"
    :text="t('pages.settings.usersPage.delete.content', { email: userFormData?.email })"
    :cancel="t('pages.settings.usersPage.crud.keep')"
    :link-text="t('global.words.delete')"
    @close="closeModal"
    @click="deleteUser"
  />
</template>
